// Whether a migration consumer acknowledges a delivery whose steps failed.
//
// A throw is what schedules Lambda's async retries and, after them, the DLQ.
// That is worth paying for a failure a retry can clear. It is pure noise for
// one only a person can resolve: the step is already recorded `failed` with
// the reason, every retry reaches the same verdict and writes nothing, and the
// dead letter costs an operator a triage that the ledger already answers.
//
// So a delivery settles — returns success — exactly when every failure in it
// is BOTH recorded on the ledger AND carries a reason registered as `human`.
// Anything else retries, and two directions of that are deliberate:
//
//   • An unrecorded failure retries even when its reason is human. The ledger
//     row is the only durable record of the verdict; acknowledging a delivery
//     whose verdict never landed would lose it.
//   • The reason must be REGISTERED as human. `classifyStepReason` sends an
//     unknown or empty code to `human` — the right default for a report, where
//     the cost of the wrong bucket is a person looking — and the wrong one
//     here, where the cost is a retry that never happens. This rule reads the
//     class map itself and treats anything absent from it as retryable.

import { MIGRATION_STEP_REASON_CLASS, stepDetailCode } from './step-manifest';

/** One failed step, as a runner reports it. */
export interface InvocationFailure {
  /** The stored reason, possibly `:`-qualified. */
  readonly reason?: string | undefined;
  /** `false` when the failure never reached the ledger. Absent means it did. */
  readonly ledgerRecorded?: boolean | undefined;
}

/**
 * `none` — nothing failed; `settle` — acknowledge, every failure waits on a
 * person and is recorded; `retry` — throw, a retry may help or the ledger does
 * not hold the verdict.
 */
export type InvocationDisposition = 'none' | 'settle' | 'retry';

/** Whether a stored reason's bare code is registered in the class map as `human`. */
export function isRegisteredHumanReason(reason: string | undefined): boolean {
  if (reason === undefined) return false;
  const code = stepDetailCode(reason);
  if (code === '' || !Object.hasOwn(MIGRATION_STEP_REASON_CLASS, code)) return false;
  return MIGRATION_STEP_REASON_CLASS[code] === 'human';
}

export function invocationDisposition(
  failures: readonly InvocationFailure[],
): InvocationDisposition {
  if (failures.length === 0) return 'none';
  const settles = failures.every(
    (failure) => failure.ledgerRecorded !== false && isRegisteredHumanReason(failure.reason),
  );
  return settles ? 'settle' : 'retry';
}
